/**
 * First-launch legal consent screen.
 *
 * A blocking, full-screen gate shown BEFORE login the first time the app is
 * opened after install (see <PrivacyConsentGate>). It presents all three legal
 * documents — Terms & Conditions, EULA, and Privacy Policy — as switchable tabs
 * (replicated, bilingual content) and requires the user to read each, then
 * actively tick an UNMARKED consent checkbox, before they can Accept (or
 * Decline/reject):
 *
 *   • Accept  → persist `privacyPolicyAccepted` and continue to the app/login.
 *   • Decline → explain that acceptance is required; on Android offer to exit
 *               the app (@capacitor/app), elsewhere keep the user blocked here.
 *
 * Reuses the same bilingual tab components as the public legal page and the
 * app's design tokens, and defaults to the device language.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Capacitor } from "@capacitor/core";
import { App as CapacitorApp } from "@capacitor/app";
import { ShieldCheck, Check } from "lucide-react";
import clsx from "clsx";
import { Button } from "@/components/ui/button";
import TermsAndConditionsTab from "@/components/common/PrivacyPolicy/TermsAndConditionsTab";
import EULATab from "@/components/common/PrivacyPolicy/EULATab";
import PrivacyPolicyTab from "@/components/common/PrivacyPolicy/PrivacyPolicyTab";
import { setPrivacyAccepted, getDevicePolicyLang, type PolicyLang } from "@/lib/privacy-consent";

type LegalTab = "tc" | "eula" | "privacyPolicy";

/** Tabs in reading order, with their (bilingual) content components. */
const TAB_ORDER: LegalTab[] = ["tc", "eula", "privacyPolicy"];
const TAB_COMPONENT: Record<LegalTab, ({ language }: { language: PolicyLang }) => ReactNode> = {
  tc: TermsAndConditionsTab,
  eula: EULATab,
  privacyPolicy: PrivacyPolicyTab,
};

/** Localized UI chrome for the consent screen (each document localizes itself). */
const UI: Record<PolicyLang, {
  title: string;
  subtitle: string;
  tabs: Record<LegalTab, string>;
  accept: string;
  decline: string;
  saving: string;
  reviewHint: string;
  declineTitle: string;
  declineBody: string;
  declineBlockedNote: string;
  exit: string;
  review: string;
  consent: string;
}> = {
  en: {
    title: "Privacy & Legal Terms",
    subtitle: "Please review all documents and accept to continue",
    tabs: {
      tc: "Terms & Conditions",
      eula: "End User License Agreement",
      privacyPolicy: "Privacy Policy",
    },
    accept: "Accept",
    decline: "Decline",
    saving: "Saving…",
    reviewHint: "Read each document to the end to continue",
    declineTitle: "Acceptance required",
    declineBody:
      "You must accept these terms to use iCheck. Without your consent we can’t provide the app’s features.",
    declineBlockedNote: "If you don’t wish to continue, please close the app from your device.",
    exit: "Exit app",
    review: "Review documents",
    consent: "By checking this box, I confirm that I have read and agree to the Privacy Policy, End User License Agreement (EULA), and Terms & Conditions.",
  },
  zh: {
    title: "隐私与法律条款",
    subtitle: "请阅读所有文件并接受后继续",
    tabs: {
      tc: "条款与条件",
      eula: "最终用户许可协议",
      privacyPolicy: "隐私政策",
    },
    accept: "接受",
    decline: "拒绝",
    saving: "正在保存…",
    reviewHint: "请将每份文件阅读至末尾以继续",
    declineTitle: "需要您的同意",
    declineBody: "您必须接受这些条款才能使用 iCheck。未经您的同意，我们无法为您提供应用的功能。",
    declineBlockedNote: "如果您不希望继续，请从设备中关闭应用。",
    exit: "退出应用",
    review: "返回阅读",
    consent: "我已阅读并同意《隐私政策》《最终用户许可协议》和《条款与条件》。",
  },
};

export default function PrivacyConsent({ onAccept }: { onAccept: () => void }) {
  const [lang, setLang] = useState<PolicyLang>("en");
  const [activeTab, setActiveTab] = useState<LegalTab>("tc");
  const [readTabs, setReadTabs] = useState<Set<LegalTab>>(new Set());
  // Explicit, ACTIVE consent: the checkbox starts UNMARKED — the user must tick
  // it themselves before Accept enables (app-store requirement: no pre-ticked
  // consent; a Decline/reject option is always available).
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const t = UI[lang];
  const ActiveDocument = TAB_COMPONENT[activeTab];
  const allRead = readTabs.size === TAB_ORDER.length;

  // Android is the only platform where an app may exit itself programmatically;
  // iOS forbids it (App Store guideline) and the web can't, so there we keep the
  // user blocked on this screen instead.
  const canExit = Capacitor.getPlatform() === "android";

  /* Default to the device language on first mount. */
  useEffect(() => {
    let alive = true;
    void getDevicePolicyLang().then((l) => {
      if (alive) setLang(l);
    });
    return () => {
      alive = false;
    };
  }, []);

  /* Mark the active document "read" once it is scrolled to the bottom. Content
   * that already fits the viewport (tablets / short docs) counts as read. */
  const checkBottom = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 24) {
      setReadTabs((prev) => {
        if (prev.has(activeTab)) return prev;
        const next = new Set(prev);
        next.add(activeTab);
        return next;
      });
    }
  }, [activeTab]);

  /* Switching tab or language swaps the content, so scroll back to the top and
   * re-evaluate the bottom gate for the newly shown document on the next frame. */
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      checkBottom();
    });
    return () => cancelAnimationFrame(id);
  }, [activeTab, lang, checkBottom]);

  /* Intercept the Android hardware back button so it can't bypass consent —
   * route it through the same Decline explanation instead. */
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    // Back opens the Decline explanation; pressing it again (dialog open) closes
    // it. Either way the user stays on the consent screen — never the login.
    const sub = CapacitorApp.addListener("backButton", () => {
      setDeclineOpen((open) => !open);
    });
    return () => {
      void sub.then((h) => h.remove());
    };
  }, []);

  async function handleAccept() {
    setSaving(true);
    await setPrivacyAccepted(true);
    // No need to drop `saving` — the gate unmounts this screen on accept.
    onAccept();
  }

  async function handleExit() {
    if (canExit) {
      await CapacitorApp.exitApp();
    } else {
      // Can't exit on this platform — keep the user blocked on the documents.
      setDeclineOpen(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-bg text-fg">
      {/* Header */}
      <header
        className="flex items-center justify-between gap-3 border-b border-border px-5 pb-3"
        style={{ paddingTop: "max(0.875rem, env(safe-area-inset-top))" }}
      >
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-primary-soft text-primary">
            <ShieldCheck className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h1 className="truncate text-base font-semibold tracking-tight">{t.title}</h1>
            <p className="truncate text-xs text-fg-muted">{t.subtitle}</p>
          </div>
        </div>

        {/* Language toggle */}
        <div className="flex shrink-0 overflow-hidden rounded-md border border-border text-xs font-medium">
          {(["en", "zh"] as const).map((l) => (
            <button
              key={l}
              type="button"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={
                lang === l
                  ? "bg-primary px-2.5 py-1 text-primary-fg"
                  : "bg-surface px-2.5 py-1 text-fg-muted hover:text-fg"
              }
            >
              {l === "en" ? "English" : "简体中文"}
            </button>
          ))}
        </div>
      </header>

      {/* Document tabs */}
      <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-border px-3" aria-label="Legal documents">
        {TAB_ORDER.map((key, i) => {
          const active = key === activeTab;
          const read = readTabs.has(key);
          return (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              aria-selected={active}
              role="tab"
              className={clsx(
                "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-sm font-medium transition-colors",
                active ? "border-primary text-primary" : "border-transparent text-fg-muted hover:text-fg",
              )}
            >
              {read ? (
                <Check className="h-3.5 w-3.5 text-success" aria-hidden />
              ) : (
                <span className="text-xs text-fg-subtle">{i + 1}.</span>
              )}
              {t.tabs[key]}
            </button>
          );
        })}
      </nav>

      {/* Scrollable document */}
      <div
        ref={scrollRef}
        onScroll={checkBottom}
        className="flex-1 overflow-y-auto overflow-x-hidden overscroll-contain"
      >
        <article className="mx-auto max-w-3xl px-5 py-6 pb-10">
          <ActiveDocument language={lang} />
        </article>
      </div>

      {/* Footer actions */}
      <footer
        className="border-t border-border bg-bg px-5 pt-3"
        style={{ paddingBottom: "max(0.875rem, env(safe-area-inset-bottom))" }}
      >
        {!allRead ? (
          <p className="mb-2 text-center text-xs text-fg-muted">
            {t.reviewHint} ({readTabs.size}/{TAB_ORDER.length})
          </p>
        ) : null}

        {/* Explicit, un-pre-ticked consent checkbox. The user must actively tick
            it to enable Accept; Decline (reject) is always available alongside. */}
        <button
          type="button"
          role="checkbox"
          aria-checked={agreed}
          onClick={() => setAgreed((v) => !v)}
          disabled={saving}
          className="mx-auto mb-3 flex w-full max-w-3xl items-start gap-2.5 text-left"
        >
          <span
            className={clsx(
              "mt-0.5 grid size-5 shrink-0 place-items-center rounded border-2 transition-colors",
              agreed ? "border-primary bg-primary text-primary-fg" : "border-border bg-surface",
            )}
          >
            {agreed ? <Check className="size-3.5" strokeWidth={3} /> : null}
          </span>
          <span className="text-xs leading-5 text-fg">{t.consent}</span>
        </button>

        <div className="mx-auto flex max-w-3xl items-center gap-3">
          <Button variant="outline" size="lg" className="flex-1" onClick={() => setDeclineOpen(true)} disabled={saving}>
            {t.decline}
          </Button>
          <Button
            size="lg"
            className="flex-1"
            onClick={handleAccept}
            disabled={!allRead || !agreed || saving}
            loading={saving}
          >
            {saving ? t.saving : t.accept}
          </Button>
        </div>
      </footer>

      {/* Decline explanation */}
      {declineOpen ? (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-5"
          role="dialog"
          aria-modal="true"
          aria-labelledby="decline-title"
        >
          <div className="w-full max-w-sm rounded-2xl border border-border bg-surface p-6 shadow-xl">
            <h2 id="decline-title" className="text-lg font-semibold text-fg">
              {t.declineTitle}
            </h2>
            <p className="mt-2 text-sm leading-6 text-fg-muted">{t.declineBody}</p>
            {!canExit ? <p className="mt-2 text-sm leading-6 text-fg-muted">{t.declineBlockedNote}</p> : null}
            <div className="mt-5 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeclineOpen(false)}>
                {t.review}
              </Button>
              {canExit ? (
                <Button variant="destructive" onClick={handleExit}>
                  {t.exit}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
